/**
 * EmbedCodeModal Component
 * Module 07 - Dashboard UI
 * Section 06 - Embeds Management (Step 6.4)
 * 
 * Modal for displaying and copying embed code with syntax highlighting
 */

import React, { useState } from 'react';
import { Copy, Check, ExternalLink } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { embedsService } from '../../services';
import { useToast } from '../../hooks';

interface EmbedCodeModalProps {
  open: boolean;
  onClose: () => void;
  embedCode: string;
  embedId: string;
  embedToken?: string;
  viewerUrl?: string;
}

/**
 * EmbedCodeModal displays the embed code with copy functionality
 * Shows both the HTML snippet and usage instructions
 */
const EmbedCodeModal: React.FC<EmbedCodeModalProps> = ({
  open,
  onClose,
  embedCode,
  embedId,
  embedToken,
  viewerUrl = import.meta.env.VITE_EMBED_BASE_URL || import.meta.env.VITE_VIEWER_BASE_URL,
}) => {
  const [copied, setCopied] = useState(false);
  const { showToast } = useToast();

  /**
   * Copy embed code to clipboard
   */
  const handleCopy = async () => {
    const success = await embedsService.copyEmbedCode(embedCode);
    
    if (success) {
      setCopied(true);
      showToast('success', 'Embed code copied to clipboard!');
      
      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } else {
      showToast('error', 'Failed to copy embed code');
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Embed Code" size="large">
      <div className="space-y-6">
        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-2">How to use this embed</h4>
          <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
            <li>Copy the code below</li>
            <li>Paste it into your website's HTML where you want the viewer to appear</li>
            <li>The viewer will automatically load and display your 3D model</li>
          </ol>
        </div>

        {/* Code Display */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              HTML Embed Code
            </label>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleCopy}
              className="flex items-center gap-2"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 text-green-600" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copy Code
                </>
              )}
            </Button>
          </div>

          <div className="relative">
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono">
              <code>{embedCode}</code>
            </pre>
          </div>
        </div>

        {/* Preview Link */}
        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Preview Embed</h4>
              <p className="text-sm text-gray-500 mt-1">
                Test your embed before deploying
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                const url = new URL(`/embed/${embedId}`, viewerUrl);
                if (embedToken) {
                  url.searchParams.set('token', embedToken);
                }
                window.open(url.toString(), '_blank');
              }}
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              Open Preview
            </Button>
          </div>
        </div>

        {/* Additional Information */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-2">Important Notes</h4>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex gap-2">
              <span className="text-primary-600 font-bold">•</span>
              <span>
                This embed will only work on domains you've specified in the allowed domains list
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary-600 font-bold">•</span>
              <span>
                The viewer requires JavaScript to be enabled in the browser
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary-600 font-bold">•</span>
              <span>
                For best performance, place the embed code where you want the viewer to appear (don't use display:none)
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary-600 font-bold">•</span>
              <span>
                The viewer is responsive and will adapt to its container's size
              </span>
            </li>
          </ul>
        </div>

        {/* Example Container */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-2">Example Container Setup</h4>
          <p className="text-sm text-gray-600 mb-3">
            Wrap your embed in a container to control its size:
          </p>
          <pre className="bg-white border border-gray-300 p-3 rounded text-sm font-mono overflow-x-auto">
            <code className="text-gray-800">{`<div style="width: 100%; max-width: 800px; height: 600px;">
  <!-- Paste embed code here -->
</div>`}</code>
          </pre>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button variant="primary" onClick={handleCopy}>
            {copied ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-2" />
                Copy Code
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EmbedCodeModal;
